/**
 * /trip/:token — Public, token-gated trip timeline.
 *
 * Designed to be shareable with family (forwardable link). Renders
 * a `<meta name="robots" content="noindex">` tag so search engines
 * won't crawl it.
 *
 * Privacy posture:
 *   - Buyer is shown by first-name initial only ("A.")
 *   - Courier is shown by display name only (no email/phone)
 *   - Destination is the CITY only (no street address)
 *   - Only `is_private=false` updates appear
 *
 * Liability posture:
 *   - Footer carries the canonical "Armstrong does not verify
 *     courier-submitted timing/location/status" disclaimer.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { AirplaneTiltIcon as AirplaneTilt, CheckCircleIcon as CheckCircle, MapPinIcon as MapPin, ShieldCheckIcon as ShieldCheck, HeartIcon as Heart, TruckIcon as Truck } from "@phosphor-icons/react";

export default function TripPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    // Tell search engines not to index this page.
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    api
      .get(`/trip/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Trip not found"));
  }, [token]);

  if (err) {
    return (
      <div
        className="min-h-screen bg-[#FBF7EC] flex items-center justify-center px-4"
        data-testid="trip-public-error"
      >
        <div className="max-w-md text-center">
          <h1 className="font-spectral text-3xl mb-3">Trip not found</h1>
          <p className="text-[13px] text-[#6B6B6B] leading-relaxed">
            {err} &middot; Ask the buyer for an updated share link.
          </p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Skeleton className="h-32 rounded-xl" />
      </div>
    );
  }

  const isIntl = data.trip_kind === "intl_lead";
  const Icon = isIntl ? AirplaneTilt : Truck;

  return (
    <div className="min-h-screen bg-[#FBF7EC]" data-testid="trip-public">
      <header className="bg-white border-b border-[#E5D08A] py-5 px-4 text-center">
        <div className="text-[10px] tracking-[3px] text-[#8A6A00] font-bold uppercase">
          Armstrong K9 Registry
        </div>
        <div className="text-[10px] text-[#8B8B8B] mt-1">
          Trip Tracker
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-white border border-[#E5D08A] rounded-xl p-6 mb-6 text-center">
          <Icon
            size={48}
            weight="duotone"
            className="text-[#8A6A00] mx-auto mb-2"
          />
          <h1 className="font-spectral text-3xl tracking-tight">
            On the way to {data.destination_city || "destination"}
          </h1>
          <p className="text-[13px] text-[#6B6B6B] mt-2">
            Trip courier:{" "}
            {data.courier_slug ? (
              <Link
                to={
                  isIntl
                    ? `/transport/international/${data.courier_slug}`
                    : `/transport/profile/${data.courier_slug}`
                }
                className="text-ink underline-offset-2 hover:underline"
              >
                {data.courier_display_name}
              </Link>
            ) : (
              <strong>{data.courier_display_name || "Courier"}</strong>
            )}
            <br />
            Buyer: <strong>{data.buyer_initial}</strong>
          </p>
          <div className="mt-3 inline-flex items-center gap-1 text-[10px] text-[#8A6A00] font-semibold bg-[#FBF7EC] border border-[#E5D08A] rounded-full px-2 py-0.5">
            <ShieldCheck size={11} weight="duotone" /> Armstrong-verified courier
          </div>
        </div>

        <h2 className="font-spectral text-xl mb-3">Timeline</h2>
        {data.updates.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E5D08A] rounded-xl p-8 text-center">
            <p className="text-[13px] text-[#6B6B6B]">
              No updates yet. Check back soon &mdash; the courier will
              post check-ins as the trip progresses.
            </p>
          </div>
        ) : (
          <ol className="relative border-l-2 border-[#E5D08A] pl-5 space-y-5">
            {data.updates.map((u) => (
              <li
                key={u.id}
                className="relative"
                data-testid={`trip-public-update-${u.id}`}
              >
                <span className="absolute -left-[26px] top-0 w-9 h-9 rounded-full bg-white border border-[#E5D08A] flex items-center justify-center">
                  {u.is_milestone ? (
                    <CheckCircle size={16} weight="duotone" className="text-[#1A6E36]" />
                  ) : (
                    <MapPin size={16} weight="duotone" className="text-[#8A6A00]" />
                  )}
                </span>
                <div className="bg-white border border-[#E5D08A] rounded-xl p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-semibold capitalize">
                      {u.status_chip.replace("_", " ")}
                    </span>
                    {u.city && (
                      <span className="text-[11px] text-[#6B6B6B]">
                        &middot; {u.city}
                      </span>
                    )}
                  </div>
                  {u.text && (
                    <p className="text-[13px] text-ink mt-1.5 leading-relaxed whitespace-pre-wrap">
                      {u.text}
                    </p>
                  )}
                  {u.photo_url && (
                    <img
                      src={u.photo_url}
                      alt="Trip update"
                      className="mt-2 rounded-lg max-w-full max-h-72 object-cover"
                    />
                  )}
                  <div className="text-[10px] text-[#8B8B8B] mt-1.5">
                    {new Date(u.created_at).toLocaleString()}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}

        <div className="mt-10 text-center text-[11px] text-[#8B8B8B] leading-relaxed">
          <Heart
            size={14}
            weight="duotone"
            className="inline text-[#C1272D] mb-0.5 mr-1"
          />
          {data.venue_disclaimer}
        </div>
      </main>
    </div>
  );
}
